"use client";
import { useApi } from "@lib/helpers/useApi";
import type { ComponentProps } from "react";
import React, { useMemo } from "react";
import { QueryKeys } from "@lib/enums/QueryKeys";
import { NextImage } from "@ecco/ui/next-image";
import { Box } from "@ecco/ui/box";
import { getExternalProviderIcons } from "./utils/getExternalProviderIcons";
import { Skeleton } from "@ecco/ui/skeleton";
import { mapPaymentMethodResponseToIcons } from "./utils/mapPaymentMethodsToIcons";
import { fetchHeapAdyenPaymentMethods } from "@lib/utils/payment-methods/fetchHeapAdyenPaymentMethods";
import { ensureActiveSession } from "@lib/auth/ensureActiveSession";
import { useAuth } from "@lib/auth/useAuth";
import { useStore } from "@store/index";
import { useLocale } from "next-intl";

type PaymentIconsProps = ComponentProps<typeof Box> & {
    /** Rendered logo height. */
    iconHeight?: ComponentProps<typeof NextImage>["h"];
    /** Rendered logo width. Defaults to the ratio implied by the intrinsic size below. */
    iconWidth?: ComponentProps<typeof NextImage>["w"];
    /**
     * Show the design's own logos where they exist, falling back to Adyen's for the rest.
     * Off by default, so the shopping bag and the current checkout keep Adyen's throughout.
     */
    preferLocalIcons?: boolean;
};

export const PaymentIcons = ({
    iconHeight = "32px",
    iconWidth = "auto",
    preferLocalIcons = false,
    ...rest
}: PaymentIconsProps) => {
    const { session, status } = useAuth();
    const cartId = useStore((state) => state.data.me.cartInfo.cart?.id);
    const { data: paymentMethodsResponse, isLoading } = useApi.useAsync(
        [QueryKeys.getPaymentMethods, cartId],
        async () => {
            if (!cartId) return { paymentMethods: [] };
            await ensureActiveSession(session);
            return fetchHeapAdyenPaymentMethods(cartId);
        },
        {
            enabled: status === "authenticated" && !!cartId,
            retry: 0,
        }
    );
    const locale = useLocale();

    // next/image reserves space from the intrinsic size, so it should describe what is actually
    // rendered. It has to be plain pixels, while the configured values are style props - so each
    // dimension is forwarded only when it is a concrete px value, and falls back to the logo's
    // own reservation otherwise. They resolve independently: an automatic width still derives
    // from the reserved ratio, so a configured height has to reach that ratio to be honoured.
    const asPixels = (value: PaymentIconsProps["iconWidth"]) =>
        typeof value === "string" && /^\d+(\.\d+)?px$/.test(value)
            ? Number.parseFloat(value)
            : undefined;
    const intrinsicWidth = (iconWidth === "auto" ? undefined : asPixels(iconWidth)) ?? 40;
    const intrinsicHeight = asPixels(iconHeight) ?? 32;

    const paymentIcons = useMemo(() => {
        if (isLoading) return [];
        const paymentMethodResponseIcons = mapPaymentMethodResponseToIcons(
            paymentMethodsResponse?.paymentMethods ?? [],
            locale,
            { preferLocalIcons }
        );
        const externalProviderIcons = getExternalProviderIcons({ preferLocalIcons });
        return [...paymentMethodResponseIcons, ...externalProviderIcons];
    }, [paymentMethodsResponse, locale, preferLocalIcons]);

    return (
        <Box
            display="flex"
            flexDirection="row"
            justifyContent="center"
            alignItems="center"
            flexWrap="wrap"
            rowGap="1"
            columnGap="2"
            {...rest}
        >
            {isLoading &&
                Array.from({ length: 6 }).map((_, index) => (
                    <Skeleton
                        key={index}
                        height={iconHeight}
                        // "auto" has no width to reserve, so fall back to the intrinsic one.
                        width={iconWidth === "auto" ? `${intrinsicWidth}px` : iconWidth}
                    />
                ))}
            {!isLoading &&
                paymentIcons.map((icon) => (
                    <NextImage
                        display="inline-flex"
                        w={iconWidth}
                        h={iconHeight}
                        key={icon.type}
                        src={icon.url}
                        // It's SVGs. No need to resize or compress.
                        unoptimized
                        alt={icon.name}
                        height={intrinsicHeight}
                        width={intrinsicWidth}
                    />
                ))}
        </Box>
    );
};
